import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCourse,
  updateCourse,
  getLessons,
  createLesson,
  updateLesson,
  deleteLesson,
  reorderCourseLessons,
  getCourseModules,
  createCourseModule,
  updateCourseModule,
  deleteCourseModule,
  uploadLessonFile,
  deleteLessonMaterial,
  getInstructors,
  getStudents,
  enrollStudent,
  unenrollStudent,
} from '@/lib/api';
import { buildEditableOutline, toReorderPayload, publishedLessonCount } from '@/lib/courseOutline';
import { toEmbedUrl, describeVideoSource, formatFileSize } from '@/lib/lessonMedia';
import LessonMaterials from '@/components/course/LessonMaterials';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  FileText,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  Upload,
  Loader2,
  Layers,
  Link2,
  Save,
  Settings2,
  Users,
  UserPlus,
  UserMinus,
  BookOpen,
} from 'lucide-react';

const EMPTY_LESSON = {
  title: '',
  content: '',
  video_url: '',
  video_type: 'none',
  duration_minutes: 30,
  order: 0,
  module_id: '',
  type: 'text',
  status: 'draft',
};

const MATERIAL_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.txt', '.csv', '.zip', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.mp3', '.m4a',
].join(',');
const VIDEO_ACCEPT = '.mp4,.webm,.mov';

const COURSE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const EMPTY_SETTINGS = {
  title: '',
  description: '',
  course_code: '',
  category: 'General',
  type: 'self-paced',
  status: 'draft',
  term: '',
  credits: 3,
  hours: 0,
  instructor_id: '',
  instructor_name: '',
};

function settingsFromCourse(course) {
  return {
    title: course?.title || '',
    description: course?.description || '',
    course_code: course?.course_code || '',
    category: course?.category || 'General',
    type: course?.type || 'self-paced',
    status: course?.status || 'draft',
    term: course?.term || '',
    credits: course?.credits ?? 3,
    hours: course?.hours ?? 0,
    instructor_id: course?.instructor_id || '',
    instructor_name: course?.instructor_name || '',
  };
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-[#062B1A]/40">{hint}</p>}
    </div>
  );
}

export default function CourseBuilder({ instructorMode = false }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingOrder, setSavingOrder] = useState(false);

  const [lessonDialog, setLessonDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [form, setForm] = useState(EMPTY_LESSON);
  const [saving, setSaving] = useState(false);

  const [moduleDialog, setModuleDialog] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({ title: '', summary: '' });

  const [syllabus, setSyllabus] = useState('');
  const [savingSyllabus, setSavingSyllabus] = useState(false);

  // Settings and roster are admin-only: instructors author content, but course
  // details and registration stay with the registrar.
  const canManageCourse = !instructorMode;
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [rosterBusy, setRosterBusy] = useState('');
  const [studentToAdd, setStudentToAdd] = useState('');

  const [uploadPct, setUploadPct] = useState(null);
  const materialInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cRes, lRes, mRes] = await Promise.all([
        getCourse(courseId),
        getLessons({ course_id: courseId }),
        getCourseModules(courseId).catch(() => ({ data: [] })),
      ]);
      setCourse(cRes.data);
      setSyllabus(cRes.data?.syllabus || '');
      setSettings(settingsFromCourse(cRes.data));
      setLessons(lRes.data || []);
      setModules(mRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load this course.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Instructor and student lists back the Settings and Students tabs, which
  // only admins see — don't fetch them for an instructor.
  useEffect(() => {
    if (!canManageCourse) return undefined;
    let cancelled = false;
    (async () => {
      const [instructorRes, studentRes] = await Promise.all([
        getInstructors().catch(() => ({ data: [] })),
        getStudents().catch(() => ({ data: [] })),
      ]);
      if (cancelled) return;
      setInstructors(instructorRes.data || []);
      const payload = studentRes.data;
      setAllStudents(Array.isArray(payload) ? payload : payload?.students || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageCourse]);

  const outline = useMemo(() => buildEditableOutline(lessons, modules), [lessons, modules]);
  // The trailing ungrouped bucket is only worth rendering when it holds something.
  const visibleGroups = outline.filter((g) => g.isModule || g.lessons.length > 0);

  // ─── Lessons ───

  const openCreateLesson = (moduleId = '') => {
    setEditingLesson(null);
    setForm({ ...EMPTY_LESSON, module_id: moduleId, order: lessons.length });
    setLessonDialog(true);
  };

  const openEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setForm({
      title: lesson.title || '',
      content: lesson.content || '',
      video_url: lesson.video_url || '',
      video_type: lesson.video_type || (lesson.video_url ? 'embed' : 'none'),
      duration_minutes: lesson.duration_minutes || 0,
      order: lesson.order || 0,
      module_id: lesson.module_id || '',
      type: lesson.type || 'text',
      status: lesson.status || 'draft',
    });
    setLessonDialog(true);
  };

  const handleSaveLesson = async () => {
    if (!form.title.trim()) {
      toast.error('A lesson title is required.');
      return;
    }
    if (form.video_type === 'embed' && form.video_url.trim() && !toEmbedUrl(form.video_url)) {
      toast.error('That video link is not one we can play. Paste a YouTube, Vimeo, Loom or Drive link.');
      return;
    }
    try {
      setSaving(true);
      // An uploaded clip's key is set by the upload flow, never by this form.
      const payload = { ...form };
      if (payload.video_type === 'upload') delete payload.video_url;
      if (editingLesson) {
        const res = await updateLesson(editingLesson.id, payload);
        setEditingLesson(res.data);
        toast.success('Lesson updated');
      } else {
        const res = await createLesson({ ...payload, course_id: courseId });
        // Stay in the dialog on create so materials can be attached right away —
        // uploads need a lesson id to hang off.
        setEditingLesson(res.data);
        toast.success('Lesson created. You can add materials now.');
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this lesson.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLesson = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"? Its video and materials are removed too.`)) return;
    try {
      await deleteLesson(lesson.id);
      toast.success('Lesson deleted');
      loadData();
    } catch {
      toast.error('Could not delete this lesson.');
    }
  };

  const togglePublish = async (lesson) => {
    const next = lesson.status === 'published' ? 'draft' : 'published';
    try {
      await updateLesson(lesson.id, { status: next });
      toast.success(next === 'published' ? 'Lesson published to students' : 'Lesson hidden from students');
      loadData();
    } catch {
      toast.error('Could not change the lesson status.');
    }
  };

  /** Move a lesson within its group and persist the whole course order. */
  const moveLesson = async (groupId, index, direction) => {
    const group = outline.find((g) => g.id === groupId);
    if (!group) return;
    const target = index + direction;
    if (target < 0 || target >= group.lessons.length) return;

    const next = outline.map((g) => {
      if (g.id !== groupId) return g;
      const reordered = [...g.lessons];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return { ...g, lessons: reordered };
    });
    // Apply locally first so the list doesn't jump while the save is in flight.
    setLessons(next.flatMap((g) => g.lessons.map((l, i) => ({ ...l, module_id: g.id, order: i }))));
    try {
      setSavingOrder(true);
      await reorderCourseLessons(courseId, toReorderPayload(next));
    } catch {
      toast.error('Could not save the new order.');
      loadData();
    } finally {
      setSavingOrder(false);
    }
  };

  const moveLessonToModule = async (lesson, moduleId) => {
    try {
      await updateLesson(lesson.id, { module_id: moduleId });
      loadData();
    } catch {
      toast.error('Could not move this lesson.');
    }
  };

  // ─── Modules ───

  const openCreateModule = () => {
    setEditingModule(null);
    setModuleForm({ title: '', summary: '' });
    setModuleDialog(true);
  };

  const openEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({ title: module.title || '', summary: module.summary || '' });
    setModuleDialog(true);
  };

  const handleSaveModule = async () => {
    if (!moduleForm.title.trim()) {
      toast.error('A module title is required.');
      return;
    }
    try {
      if (editingModule) {
        await updateCourseModule(courseId, editingModule.id, moduleForm);
        toast.success('Module updated');
      } else {
        await createCourseModule(courseId, { ...moduleForm, order: modules.length });
        toast.success('Module added');
      }
      setModuleDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this module.');
    }
  };

  const handleDeleteModule = async (module) => {
    if (
      !window.confirm(
        `Delete the module "${module.title}"? Lessons inside it are kept and moved to Course Content.`
      )
    )
      return;
    try {
      await deleteCourseModule(courseId, module.id);
      toast.success('Module deleted');
      loadData();
    } catch {
      toast.error('Could not delete this module.');
    }
  };

  // ─── Uploads ───

  const handleUpload = async (file, kind) => {
    if (!file || !editingLesson) return;
    try {
      setUploadPct(0);
      await uploadLessonFile(editingLesson.id, file, { kind, onProgress: setUploadPct });
      toast.success(kind === 'video' ? 'Video uploaded' : 'Material added');
      const refreshed = await getLessons({ course_id: courseId });
      setLessons(refreshed.data || []);
      const updated = (refreshed.data || []).find((l) => l.id === editingLesson.id);
      if (updated) {
        setEditingLesson(updated);
        if (kind === 'video') {
          setForm((f) => ({ ...f, video_type: 'upload', type: 'video' }));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed.');
    } finally {
      setUploadPct(null);
    }
  };

  const handleRemoveMaterial = async (material) => {
    if (!editingLesson) return;
    try {
      await deleteLessonMaterial(editingLesson.id, material.id);
      toast.success('Material removed');
      const refreshed = await getLessons({ course_id: courseId });
      setLessons(refreshed.data || []);
      setEditingLesson((refreshed.data || []).find((l) => l.id === editingLesson.id) || editingLesson);
    } catch {
      toast.error('Could not remove this material.');
    }
  };

  // ─── Settings & roster (admin only) ───

  const handleSaveSettings = async () => {
    if (!settings.title.trim()) {
      toast.error('A course title is required.');
      return;
    }
    try {
      setSavingSettings(true);
      const res = await updateCourse(courseId, settings);
      setCourse(res.data);
      setSettings(settingsFromCourse(res.data));
      toast.success('Course settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the course settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const refreshCourse = async () => {
    const res = await getCourse(courseId);
    setCourse(res.data);
  };

  const handleEnroll = async () => {
    if (!studentToAdd) return;
    try {
      setRosterBusy(studentToAdd);
      await enrollStudent(courseId, studentToAdd);
      toast.success('Student registered in this course');
      setStudentToAdd('');
      await refreshCourse();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not register this student.');
    } finally {
      setRosterBusy('');
    }
  };

  const handleUnenroll = async (student) => {
    if (!window.confirm(`Withdraw ${student.name} from this course?`)) return;
    try {
      setRosterBusy(student.id);
      await unenrollStudent(courseId, student.id);
      toast.success('Student withdrawn');
      await refreshCourse();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not withdraw this student.');
    } finally {
      setRosterBusy('');
    }
  };

  const handleSaveSyllabus = async () => {
    try {
      setSavingSyllabus(true);
      await updateCourse(courseId, { syllabus });
      toast.success('Syllabus saved');
      setCourse((c) => ({ ...c, syllabus }));
    } catch {
      toast.error('Could not save the syllabus.');
    } finally {
      setSavingSyllabus(false);
    }
  };

  if (loading) return <div className="h-96 animate-pulse rounded-xl bg-white" />;

  const publishedCount = publishedLessonCount(lessons);
  const enrolledIds = new Set(course?.students_enrolled || []);
  const roster = allStudents.filter((student) => enrolledIds.has(student.id));
  const enrollable = allStudents.filter((student) => !enrolledIds.has(student.id));

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(instructorMode ? `/instructor/courses/${courseId}` : '/admin/courses')}
          className="text-[#062B1A]/60"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {course?.title || 'Course'}
            </h2>
            {course?.course_code && (
              <Badge variant="outline" className="border-[#062B1A]/20 text-[#062B1A]/70">
                {course.course_code}
              </Badge>
            )}
            <Badge
              variant="outline"
              className={
                course?.status === 'published'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-amber-300 bg-amber-50 text-amber-700'
              }
            >
              {course?.status || 'draft'}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            {publishedCount} of {lessons.length} lessons visible to students
            {course?.term ? ` • ${course.term}` : ''}
            {canManageCourse ? ` • ${enrolledIds.size} registered` : ''}
          </p>
        </div>
        {savingOrder && <Loader2 className="h-4 w-4 animate-spin text-[#062B1A]/40" />}
      </div>

      <Tabs defaultValue="content">
        <TabsList className="bg-[#F7F8FA]">
          <TabsTrigger value="content" className="text-xs data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
            <Layers className="mr-1.5 h-3.5 w-3.5" /> Content
          </TabsTrigger>
          <TabsTrigger value="syllabus" className="text-xs data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Syllabus
          </TabsTrigger>
          {canManageCourse && (
            <TabsTrigger value="settings" className="text-xs data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
              <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Settings
            </TabsTrigger>
          )}
          {canManageCourse && (
            <TabsTrigger value="students" className="text-xs data-[state=active]:bg-[#062B1A] data-[state=active]:text-white">
              <Users className="mr-1.5 h-3.5 w-3.5" /> Students
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="content" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="border-[rgba(6,43,26,0.15)]" onClick={openCreateModule}>
              <Layers className="mr-2 h-4 w-4" /> Add Module
            </Button>
            <Button className="btn-hover bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={() => openCreateLesson()}>
              <Plus className="mr-2 h-4 w-4" /> Add Lesson
            </Button>
          </div>

          {lessons.length === 0 && modules.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="mx-auto mb-3 h-12 w-12 text-[#062B1A]/15" />
              <p className="mb-1 text-[#062B1A]/60">No content yet</p>
              <p className="mx-auto mb-4 max-w-sm text-sm text-[#062B1A]/40">
                Group your course into modules (weeks or units), then add lessons with video,
                notes and downloadable materials.
              </p>
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={() => openCreateLesson()}>
                Add the first lesson
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {visibleGroups.map((group) => (
                <div key={group.id || 'ungrouped'}>
                  <div className="mb-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#062B1A]">
                        {group.title}
                        <span className="ml-2 text-xs font-normal text-[#062B1A]/40">
                          {group.lessons.length} lesson{group.lessons.length === 1 ? '' : 's'}
                        </span>
                      </p>
                      {group.summary && <p className="mt-0.5 text-xs text-[#062B1A]/45">{group.summary}</p>}
                    </div>
                    {group.isModule && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditModule(group)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-500"
                          onClick={() => handleDeleteModule(group)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-[#062B1A]/60"
                      onClick={() => openCreateLesson(group.id)}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Lesson
                    </Button>
                  </div>

                  {group.lessons.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-[#062B1A]/12 px-3 py-4 text-center text-xs text-[#062B1A]/35">
                      Empty module — add a lesson or move one here.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {group.lessons.map((lesson, idx) => (
                        <Card key={lesson.id} className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                          <CardContent className="flex items-center gap-3 p-3">
                            <div className="flex flex-col">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                disabled={idx === 0}
                                onClick={() => moveLesson(group.id, idx, -1)}
                                aria-label="Move up"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5"
                                disabled={idx === group.lessons.length - 1}
                                onClick={() => moveLesson(group.id, idx, 1)}
                                aria-label="Move down"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#062B1A]/5 text-xs font-bold text-[#062B1A]">
                              {idx + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="truncate text-sm font-medium text-[#062B1A]">{lesson.title}</h3>
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${
                                    lesson.status === 'draft'
                                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                                      : 'border-emerald-300 bg-emerald-50 text-emerald-700'
                                  }`}
                                >
                                  {lesson.status === 'draft' ? 'Draft' : 'Published'}
                                </Badge>
                                {lesson.video_type === 'upload' && (
                                  <Badge variant="outline" className="text-[10px]">Video file</Badge>
                                )}
                                {lesson.video_type === 'embed' && lesson.video_url && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {describeVideoSource(lesson.video_url)}
                                  </Badge>
                                )}
                                {(lesson.materials || []).length > 0 && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {lesson.materials.length} file{lesson.materials.length === 1 ? '' : 's'}
                                  </Badge>
                                )}
                              </div>
                              <p className="mt-0.5 text-xs text-[#062B1A]/40">
                                {lesson.duration_minutes} min
                              </p>
                            </div>
                            {modules.length > 0 && (
                              <Select
                                value={lesson.module_id || 'none'}
                                onValueChange={(v) => moveLessonToModule(lesson, v === 'none' ? '' : v)}
                              >
                                <SelectTrigger className="hidden h-8 w-[150px] border-[rgba(6,43,26,0.12)] text-xs md:flex">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Course Content</SelectItem>
                                  {modules.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <div className="flex flex-shrink-0 items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                title={lesson.status === 'published' ? 'Hide from students' : 'Publish to students'}
                                onClick={() => togglePublish(lesson)}
                              >
                                {lesson.status === 'published' ? (
                                  <Eye className="h-3.5 w-3.5 text-emerald-600" />
                                ) : (
                                  <EyeOff className="h-3.5 w-3.5 text-amber-600" />
                                )}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditLesson(lesson)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-rose-500"
                                onClick={() => handleDeleteLesson(lesson)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="syllabus" className="mt-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-3 p-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Course syllabus</label>
                <p className="mb-2 text-xs text-[#062B1A]/45">
                  Shown to enrolled students on the course page. Cover objectives, weekly outline,
                  assessment weighting, required texts and course policies.
                </p>
                <Textarea
                  value={syllabus}
                  onChange={(e) => setSyllabus(e.target.value)}
                  placeholder={'Course description\n\nLearning outcomes\n\nAssessment breakdown\n\nPolicies'}
                  className="min-h-[320px] border-[rgba(6,43,26,0.12)] font-mono text-sm"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                  onClick={handleSaveSyllabus}
                  disabled={savingSyllabus}
                >
                  {savingSyllabus ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save syllabus
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canManageCourse && (
          <TabsContent value="settings" className="mt-4">
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="space-y-5 p-5">
                <Field label="Title *">
                  <Input
                    value={settings.title}
                    onChange={(e) => setSettings((s) => ({ ...s, title: e.target.value }))}
                    placeholder="Course title"
                    className="border-[rgba(6,43,26,0.12)]"
                  />
                </Field>

                <Field label="Description" hint="Shown in the course catalog and on course cards.">
                  <Textarea
                    value={settings.description}
                    onChange={(e) => setSettings((s) => ({ ...s, description: e.target.value }))}
                    placeholder="What this course covers"
                    className="min-h-[90px] border-[rgba(6,43,26,0.12)]"
                  />
                </Field>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Field label="Course code">
                    <Input
                      value={settings.course_code}
                      onChange={(e) => setSettings((s) => ({ ...s, course_code: e.target.value }))}
                      placeholder="MGMT338"
                      className="border-[rgba(6,43,26,0.12)]"
                    />
                  </Field>
                  <Field label="Term">
                    <Input
                      value={settings.term}
                      onChange={(e) => setSettings((s) => ({ ...s, term: e.target.value }))}
                      placeholder="Fall 2026"
                      className="border-[rgba(6,43,26,0.12)]"
                    />
                  </Field>
                  <Field label="Credits">
                    <Input
                      type="number"
                      min={0}
                      value={settings.credits}
                      onChange={(e) => setSettings((s) => ({ ...s, credits: parseInt(e.target.value, 10) || 0 }))}
                      className="border-[rgba(6,43,26,0.12)]"
                    />
                  </Field>
                  <Field label="Hours">
                    <Input
                      type="number"
                      min={0}
                      value={settings.hours}
                      onChange={(e) => setSettings((s) => ({ ...s, hours: parseInt(e.target.value, 10) || 0 }))}
                      className="border-[rgba(6,43,26,0.12)]"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="Category">
                    <Input
                      value={settings.category}
                      onChange={(e) => setSettings((s) => ({ ...s, category: e.target.value }))}
                      placeholder="Academic"
                      className="border-[rgba(6,43,26,0.12)]"
                    />
                  </Field>
                  <Field label="Delivery type">
                    <Select value={settings.type} onValueChange={(v) => setSettings((s) => ({ ...s, type: v }))}>
                      <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self-paced">Self-Paced</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Status" hint="Draft courses stay out of the student catalog.">
                    <Select value={settings.status} onValueChange={(v) => setSettings((s) => ({ ...s, status: v }))}>
                      <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COURSE_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Instructor" hint="The instructor assigned here gets this course in their workspace.">
                  <Select
                    value={settings.instructor_id || '_none'}
                    onValueChange={(value) => {
                      const instructorId = value === '_none' ? '' : value;
                      const instructor = instructors.find((item) => item.id === instructorId);
                      setSettings((s) => ({
                        ...s,
                        instructor_id: instructorId,
                        instructor_name: instructor?.name || '',
                      }));
                    }}
                  >
                    <SelectTrigger className="border-[rgba(6,43,26,0.12)]">
                      <SelectValue placeholder="Select instructor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No instructor</SelectItem>
                      {instructors.map((instructor) => (
                        <SelectItem key={instructor.id} value={instructor.id}>{instructor.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="flex justify-end border-t border-[#062B1A]/8 pt-4">
                  <Button
                    className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    onClick={handleSaveSettings}
                    disabled={savingSettings}
                  >
                    {savingSettings ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canManageCourse && (
          <TabsContent value="students" className="mt-4 space-y-4">
            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-5">
                <p className="mb-1 text-sm font-semibold text-[#062B1A]">Register a student</p>
                <p className="mb-3 text-xs text-[#062B1A]/45">
                  Course registration is handled here. Students cannot add or drop courses
                  themselves.
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={studentToAdd} onValueChange={setStudentToAdd}>
                    <SelectTrigger className="border-[rgba(6,43,26,0.12)] sm:max-w-md">
                      <SelectValue placeholder="Select a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {enrollable.length === 0 && (
                        <div className="px-3 py-2 text-xs text-[#062B1A]/45">
                          Every student is already registered.
                        </div>
                      )}
                      {enrollable.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.name}
                          {student.student_number ? ` — ${student.student_number}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    onClick={handleEnroll}
                    disabled={!studentToAdd || rosterBusy !== ''}
                  >
                    <UserPlus className="mr-2 h-4 w-4" /> Register
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
              <CardContent className="p-5">
                <p className="mb-3 text-sm font-semibold text-[#062B1A]">
                  Registered students
                  <span className="ml-2 text-xs font-normal text-[#062B1A]/40">{roster.length}</span>
                </p>
                {roster.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Users className="h-8 w-8 text-[#062B1A]/15" />
                    <p className="text-sm text-[#062B1A]/50">No students registered in this course yet.</p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {roster.map((student) => (
                      <li
                        key={student.id}
                        className="flex items-center gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] p-3"
                      >
                        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#062B1A]/5">
                          <BookOpen className="h-4 w-4 text-[#062B1A]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-[#062B1A]">{student.name}</p>
                          <p className="truncate text-xs text-[#062B1A]/45">
                            {student.student_number || '—'}
                            {student.email ? ` · ${student.email}` : ''}
                          </p>
                        </div>
                        {student.enrollment_status && (
                          <Badge variant="outline" className="hidden shrink-0 text-[10px] sm:inline-flex">
                            {student.enrollment_status}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-shrink-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          disabled={rosterBusy === student.id}
                          onClick={() => handleUnenroll(student)}
                        >
                          {rosterBusy === student.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <UserMinus className="h-3.5 w-3.5" />
                          )}
                          <span className="ml-1.5 hidden sm:inline">Withdraw</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ─── Lesson editor ─── */}
      <Dialog open={lessonDialog} onOpenChange={(open) => { setLessonDialog(open); if (!open) setUploadPct(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {editingLesson ? 'Edit Lesson' : 'Add Lesson'}
            </DialogTitle>
            <DialogDescription>
              {editingLesson
                ? 'Changes are saved to this lesson. Uploads apply immediately.'
                : 'Save the lesson first, then attach video and materials.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Title *</label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Lesson title"
                className="border-[rgba(6,43,26,0.12)]"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Module</label>
                <Select
                  value={form.module_id || 'none'}
                  onValueChange={(v) => setForm((f) => ({ ...f, module_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Course Content</SelectItem>
                    {modules.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Duration (min)</label>
                <Input
                  type="number"
                  min="0"
                  value={form.duration_minutes}
                  onChange={(e) => setForm((f) => ({ ...f, duration_minutes: parseInt(e.target.value, 10) || 0 }))}
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Visibility</label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft — hidden</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Lesson notes</label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="What students should read or do for this lesson..."
                className="min-h-[120px] border-[rgba(6,43,26,0.12)]"
              />
            </div>

            {/* Video */}
            <div className="rounded-lg border border-[rgba(6,43,26,0.1)] bg-[#F7F8FA] p-4">
              <label className="mb-2 block text-sm font-semibold text-[#062B1A]">Video</label>
              <Select
                value={form.video_type}
                onValueChange={(v) => setForm((f) => ({ ...f, video_type: v, type: v === 'none' ? 'text' : 'video' }))}
              >
                <SelectTrigger className="border-[rgba(6,43,26,0.12)] bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No video</SelectItem>
                  <SelectItem value="embed">Link (YouTube, Vimeo, Loom, Drive)</SelectItem>
                  <SelectItem value="upload">Upload a clip</SelectItem>
                </SelectContent>
              </Select>

              {form.video_type === 'embed' && (
                <div className="mt-3">
                  <Input
                    value={form.video_url}
                    onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="border-[rgba(6,43,26,0.12)] bg-white"
                  />
                  {form.video_url.trim() &&
                    (toEmbedUrl(form.video_url) ? (
                      <p className="mt-1.5 flex items-center gap-1 text-xs text-emerald-700">
                        <Link2 className="h-3 w-3" /> {describeVideoSource(form.video_url)} link recognised
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs text-rose-600">
                        This link can't be embedded. Use the share or watch URL from the provider.
                      </p>
                    ))}
                </div>
              )}

              {form.video_type === 'upload' && (
                <div className="mt-3">
                  {!editingLesson ? (
                    <p className="text-xs text-[#062B1A]/50">Save the lesson first, then upload a clip.</p>
                  ) : (
                    <>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept={VIDEO_ACCEPT}
                        className="hidden"
                        onChange={(e) => {
                          handleUpload(e.target.files?.[0], 'video');
                          e.target.value = '';
                        }}
                      />
                      <div className="flex flex-wrap items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          className="border-[rgba(6,43,26,0.15)] bg-white"
                          disabled={uploadPct !== null}
                          onClick={() => videoInputRef.current?.click()}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          {editingLesson.video_key ? 'Replace clip' : 'Choose video'}
                        </Button>
                        {editingLesson.video_filename && (
                          <span className="text-xs text-[#062B1A]/55">
                            {editingLesson.video_filename}
                            {editingLesson.video_size ? ` • ${formatFileSize(editingLesson.video_size)}` : ''}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-xs text-[#062B1A]/40">MP4, WebM or MOV, up to 500 MB.</p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Materials */}
            <div className="rounded-lg border border-[rgba(6,43,26,0.1)] bg-[#F7F8FA] p-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-semibold text-[#062B1A]">Materials</label>
                {editingLesson && (
                  <>
                    <input
                      ref={materialInputRef}
                      type="file"
                      accept={MATERIAL_ACCEPT}
                      className="hidden"
                      onChange={(e) => {
                        handleUpload(e.target.files?.[0], 'material');
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-[rgba(6,43,26,0.15)] bg-white"
                      disabled={uploadPct !== null}
                      onClick={() => materialInputRef.current?.click()}
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" /> Add file
                    </Button>
                  </>
                )}
              </div>
              {editingLesson ? (
                <LessonMaterials
                  lessonId={editingLesson.id}
                  materials={editingLesson.materials}
                  onRemove={handleRemoveMaterial}
                  emptyLabel="No materials yet. Add slides, readings or worksheets."
                />
              ) : (
                <p className="text-xs text-[#062B1A]/50">Save the lesson first, then attach materials.</p>
              )}
              {editingLesson && (
                <p className="mt-2 text-xs text-[#062B1A]/40">
                  PDF, Word, Excel, PowerPoint, images, audio, text or ZIP — up to 50 MB each.
                </p>
              )}
            </div>

            {uploadPct !== null && (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-[#062B1A]/55">
                  <span>Uploading…</span>
                  <span>{uploadPct}%</span>
                </div>
                <Progress value={uploadPct} className="h-2 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog(false)}>
              {editingLesson ? 'Done' : 'Cancel'}
            </Button>
            <Button
              className="bg-[#D4AF37] text-black hover:bg-[#B8941F]"
              onClick={handleSaveLesson}
              disabled={saving || uploadPct !== null}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingLesson ? 'Save changes' : 'Create lesson'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Module editor ─── */}
      <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {editingModule ? 'Edit Module' : 'Add Module'}
            </DialogTitle>
            <DialogDescription>
              Modules group lessons into weeks or units on the student's course page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Title *</label>
              <Input
                value={moduleForm.title}
                onChange={(e) => setModuleForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Week 1 — Foundations"
                className="border-[rgba(6,43,26,0.12)]"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Summary</label>
              <Textarea
                value={moduleForm.summary}
                onChange={(e) => setModuleForm((f) => ({ ...f, summary: e.target.value }))}
                placeholder="What this module covers (optional)"
                className="min-h-[80px] border-[rgba(6,43,26,0.12)]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSaveModule}>
              {editingModule ? 'Save' : 'Add module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
